import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";
import debounce from "lodash-es/debounce";
import { useSearchParams } from "react-router";

import { URL_SEARCH_KEY } from "constants/urlQueryKeys";
import { Cross, Magnify } from "components/icons/generated";
import Input from "ds/components/Input";
import { getSessionStorageKeys } from "components/Filters/helpers";
import ButtonIcon from "ds/components/ButtonIcon";
import Icon from "ds/components/Icon";

import styles from "./styles.module.css";

type SearchInputProps = {
  placeholder?: string;
  filtersOrderSettingsKey: string;
  disabled?: boolean;
  callback?: () => void;
};

const SearchInput = ({
  placeholder,
  filtersOrderSettingsKey,
  disabled,
  callback,
}: SearchInputProps) => {
  const storage = sessionStorage;
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { storageUrlSearchKey } = getSessionStorageKeys(filtersOrderSettingsKey);
  const urlSearchInput = decodeURIComponent(
    searchParams.get(URL_SEARCH_KEY) || storage.getItem(storageUrlSearchKey) || ""
  );

  const [searchInput, setSearchInput] = useState(urlSearchInput);
  const [isDebouncePending, setIsDebouncePending] = useState(false);

  const handleUpdateURLParams = useCallback(
    (value: string) => {
      setIsDebouncePending(false);

      let urlParameters = searchParams;

      // verifying if window location search has been modified by other component which leads to effect where debounced references are outdated
      if (location.search !== window.location.search) {
        urlParameters = new URLSearchParams(window.location.search);
      }

      if (value) {
        urlParameters.set(URL_SEARCH_KEY, encodeURIComponent(value));
        storage.setItem(storageUrlSearchKey, encodeURIComponent(value));
      } else {
        urlParameters.delete(URL_SEARCH_KEY);
        storage.removeItem(storageUrlSearchKey);
      }

      callback?.();
      setSearchParams(urlParameters);
    },
    [callback, searchParams, storage, storageUrlSearchKey, setSearchParams]
  );

  const debouncedUpdateURLParams = useMemo(
    // TODO: filter refactor - sync query with local state
    () => debounce(handleUpdateURLParams, 300),
    [handleUpdateURLParams]
  );

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsDebouncePending(true);
    setSearchInput(e.target.value);
    debouncedUpdateURLParams(e.target.value);
  };

  const handleClear = useCallback(() => {
    debouncedUpdateURLParams.cancel();
    setSearchInput("");
    handleUpdateURLParams("");
    inputRef.current?.focus();
  }, [debouncedUpdateURLParams, handleUpdateURLParams]);

  useEffect(() => {
    // reset the 'searchInput' state when URL hasn't URL_SEARCH_KEY param
    if (!searchParams.has(URL_SEARCH_KEY) && searchInput.length > 0 && !isDebouncePending) {
      setSearchInput("");
    }
    // set the 'searchInput' state when URL has URL_SEARCH_KEY param
    else if (searchParams.has(URL_SEARCH_KEY)) {
      const value = searchParams.get(URL_SEARCH_KEY);
      if (value && value !== storage.getItem(storageUrlSearchKey)) {
        storage.setItem(storageUrlSearchKey, value);
      }

      // check if user is not typing in input
      if (inputRef.current !== document.activeElement && value) {
        const decodedValue = decodeURIComponent(value);

        if (decodedValue !== searchInput) {
          setSearchInput(decodedValue);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, storage]);

  return (
    <div className={styles.searchInput}>
      <Input
        ref={inputRef}
        className={cx(styles.input, { [styles.withClear]: !!searchInput })}
        type="text"
        onChange={handleSearchInputChange}
        placeholder={placeholder}
        value={searchInput}
        disabled={disabled}
      />

      <Icon className={styles.icon} src={Magnify} />

      {!!searchInput && (
        <ButtonIcon
          className={styles.clearButton}
          variant="ghost"
          icon={Cross}
          disabled={disabled}
          onPress={handleClear}
        >
          Clear
        </ButtonIcon>
      )}
    </div>
  );
};

export default memo(SearchInput);
